import React from 'react';
import './RunnerCv.css';

const PROFILE_SRC = '/images/profile.jpg';

const postJobs = [
  {
    company: 'Truth Creative',
    companyHref: 'https://www.truth-creative.co.uk/',
    location: null,
    badge: 'Contract',
    dates: 'Jun 2025 – Feb 2026',
    title: 'Motion Designer & Videographer',
    groups: [
      {
        label: 'In post',
        bullets: [
          'Edited raw footage to final delivery: multicam sync, cut to script and colour grade in DaVinci Resolve',
          'Keyed and composited green screen footage and built motion graphics',
          'Organised media from shoot through versioning and export, and QC checked deliverables before client handoff',
          'Ran client review rounds and turned around edit amends to deadline',
        ],
      },
      {
        label: 'On set',
        bullets: [
          'Led small-crew branded shoots at client sites across the UK',
          'Set up lighting, camera and audio for client interviews, green screen capture and multicam shoots',
        ],
      },
    ],
  },
  {
    company: 'Box Bear',
    companyHref: 'https://boxbear.co.uk/',
    location: null,
    badge: 'In-house studio',
    dates: 'May 2022 – May 2024',
    title: 'Motion Graphic Designer & Production Assistant',
    groups: [
      {
        label: 'In post',
        bullets: [
          "Edited footage into finished films shaped to each client's brief, including colour and graphics through to final export",
          'Responsible for organisation of media and versioning on each project through to final delivery',
          'Designed motion graphics in After Effects and 3D assets in Cinema 4D, assembled in Premiere Pro',
        ],
      },
      {
        label: 'In the studio',
        bullets: [
          'Prepped the in-house studio for shoots and live sessions, including cameras, lighting and audio',
          'Ran multicamera and live-stream sessions with healthcare professionals joining from around the world',
        ],
      },
    ],
  },
  {
    company: 'Freelance',
    companyHref: 'https://uk.linkedin.com/in/seanderham',
    location: null,
    badge: 'Alongside employed roles',
    dates: 'Jun 2021 – May 2025',
    title: 'Motion Designer & Editor',
    bullets: [
      'Edited multicamera interviews and motion content for clients across sectors',
      'Took projects from offline edit through colour and graphics to finished deliverables',
    ],
  },
];

const hospitalityJob = {
  company: 'The Championships, Wimbledon',
  companyHref: 'https://www.wimbledon.com/',
  badge: 'Seasonal',
  dates: '2018, 2019, 2021',
  title: 'Media Bar & Restaurant Team Lead',
  bullets: [
    'Ran the media bar and restaurant for press and players across the Championships fortnight',
    'Built shift rotas, trained new starters, placed orders and kept stock under control',
    'Handled guest requests directly and kept service moving through the busiest sessions',
  ],
};

const otherJob = {
  company: 'The Adare Collection',
  companyHref: 'https://theadarecollection.com/',
  badge: 'Remote',
  dates: 'Mar 2025 – present',
  title: 'Lead Web Designer',
  bullets: [
    'Designed and coded the company website from a blank file, including interactive maps and virtual tours',
    'Look after the live site day to day, working with the team to get new features out on deadline',
  ],
};

const skills = [
  'DaVinci Resolve: colour grade, multicam sync, media management and export QC',
  'Adobe Premiere Pro and After Effects: offline edits, conforms, motion graphics',
  'Media organisation, versioning, render workflows and deliverable QC',
  'Studio and session prep, including kit setup and client-facing support',
  'Blender, Cinema 4D and Unreal Engine: how CG and VFX sit alongside colour',
];

const JobEntry = ({ job }) => (
  <div className="runner-cv__job">
    <div className="runner-cv__job-top">
      <h3>
        {job.companyHref ? (
          <a href={job.companyHref} target="_blank" rel="noopener noreferrer">
            {job.company}
          </a>
        ) : (
          job.company
        )}
        {job.location ? (
          <span className="runner-cv__badge runner-cv__badge--quiet">
            {job.location}
          </span>
        ) : null}
        {job.badge ? <span className="runner-cv__badge">{job.badge}</span> : null}
      </h3>
      <span className="runner-cv__dates">{job.dates}</span>
    </div>
    <h4>{job.title}</h4>
    {job.groups ? (
      <div className="runner-cv__groups">
        {job.groups.map((group) => (
          <div className="runner-cv__group" key={group.label}>
            <p className="runner-cv__group-label">{group.label}</p>
            <ul className="runner-cv__bullets">
              {group.bullets.map((bullet) => (
                <li key={bullet}>{bullet}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    ) : job.bullets ? (
      <ul className="runner-cv__bullets">
        {job.bullets.map((bullet) => (
          <li key={bullet}>{bullet}</li>
        ))}
      </ul>
    ) : (
      <p>{job.body}</p>
    )}
  </div>
);

// Unlinked page: reachable only by typing the URL directly. Kept free of the
// site header and footer so it reads as a standalone CV.
const ColourCv = () => {
  return (
    <div className="runner-cv-page">
      <article className="runner-cv">
        <header className="runner-cv__header">
          <div className="runner-cv__intro">
            <h1 className="runner-cv__name">Sean Derham</h1>
            <p className="runner-cv__role">Colour Assistant</p>
            <p className="runner-cv__location">
              London · Available immediately
            </p>
            <p className="runner-cv__licence">Full UK driving licence</p>
            <div className="runner-cv__contacts runner-cv__contacts--screen">
              <a href="tel:+447597368482">07597 368482</a>
              <a href="mailto:sjderham@protonmail.com">sjderham@protonmail.com</a>
              <a
                href="https://seanderham.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                seanderham.com
              </a>
              <a
                href="https://uk.linkedin.com/in/seanderham"
                target="_blank"
                rel="noopener noreferrer"
              >
                LinkedIn
              </a>
            </div>
            <div className="runner-cv__contacts runner-cv__contacts--print">
              <span>
                07597 368482 · sjderham@protonmail.com · seanderham.com ·
                linkedin.com/in/seanderham
              </span>
            </div>
          </div>
          <img
            className="runner-cv__photo"
            src={PROFILE_SRC}
            alt="Sean Derham"
            width={112}
            height={112}
          />
        </header>

        <section className="runner-cv__section">
          <h2>Profile</h2>
          <p>
            Four years in post-production and on shoot days have given me a
            solid grounding in how colour sits inside a production. Most of my
            time has been in the edit: organising media, cutting, grading in
            DaVinci Resolve for final export. I want to build a career in
            colour, and I&apos;m looking for a Colour Assistant role where I can
            support colourists day to day, learn studio workflows properly, and
            work my way towards becoming a colourist myself. In my spare time I
            shoot and grade my own short films on a Sony FX3.
          </p>
        </section>

        <section className="runner-cv__section" id="cv-work">
          <h2>Colour &amp; Post Experience</h2>
          <div className="runner-cv__jobs">
            {postJobs.map((job) => (
              <JobEntry key={`${job.company}-${job.dates}`} job={job} />
            ))}
          </div>
        </section>

        <section className="runner-cv__section">
          <h2>Client &amp; Studio Support</h2>
          <div className="runner-cv__jobs">
            <JobEntry job={hospitalityJob} />
          </div>
        </section>

        <section className="runner-cv__section">
          <h2>Other Employment</h2>
          <div className="runner-cv__jobs">
            <JobEntry job={otherJob} />
          </div>
        </section>

        <section className="runner-cv__section">
          <h2>Personal Projects</h2>
          <p>
            I shoot and grade short films on my own Sony FX3, using personal
            work to push colour and lighting ideas separate from a client brief.
          </p>
        </section>

        <section className="runner-cv__section">
          <h2>Skills &amp; Software</h2>
          <ul className="runner-cv__skill-list">
            {skills.map((skill) => (
              <li key={skill}>{skill}</li>
            ))}
          </ul>
        </section>

        <section className="runner-cv__section">
          <h2>Education</h2>
          <div className="runner-cv__job">
            <div className="runner-cv__job-top">
              <h3>University of Sussex</h3>
              <span className="runner-cv__dates">2018 – 2021</span>
            </div>
            <p>First Class Honours, Product Design</p>
          </div>
        </section>

        <section className="runner-cv__section">
          <h2>Training</h2>
          <ul className="runner-cv__skill-list">
            <li>ScreenSkills e-learning modules completed</li>
          </ul>
        </section>

        <p className="runner-cv__disclaimer">
          This CV may be kept on file and distributed for employment purposes.
        </p>
      </article>
    </div>
  );
};

export default ColourCv;
